import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { 
  Landmark, 
  Loader2, 
  CheckCircle,
  AlertCircle
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface BankPayoutsEditorProps {
  merchantId: string;
}

export function BankPayoutsEditor({ merchantId }: BankPayoutsEditorProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settingsId, setSettingsId] = useState<string | null>(null);
  const [form, setForm] = useState({
    bank_account_name: '',
    bank_name: '',
    bank_account_number: '',
    bank_ifsc: '',
  });

  useEffect(() => {
    fetchBankDetails();
  }, [merchantId]);

  const fetchBankDetails = async () => {
    try {
      const { data, error } = await supabase
        .from('professional_invoice_settings')
        .select('id, bank_account_name, bank_name, bank_account_number, bank_ifsc')
        .eq('merchant_id', merchantId)
        .maybeSingle();

      if (!error && data) {
        setSettingsId(data.id);
        setForm({
          bank_account_name: data.bank_account_name || '',
          bank_name: data.bank_name || '',
          bank_account_number: data.bank_account_number || '',
          bank_ifsc: data.bank_ifsc || '',
        });
      }
    } catch (err) {
      console.error('Error fetching bank details:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        merchant_id: merchantId,
        bank_account_name: form.bank_account_name || null,
        bank_name: form.bank_name || null,
        bank_account_number: form.bank_account_number || null,
        bank_ifsc: form.bank_ifsc || null,
      };

      if (settingsId) {
        const { error } = await supabase
          .from('professional_invoice_settings')
          .update(payload)
          .eq('id', settingsId);
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from('professional_invoice_settings')
          .insert({ ...payload, invoice_enabled: false })
          .select('id')
          .single();
        if (error) throw error;
        setSettingsId(data.id);
      }

      toast.success('Bank details saved!');
    } catch (err) {
      console.error('Error saving bank details:', err);
      toast.error('Failed to save bank details');
    } finally {
      setSaving(false);
    }
  };

  const isIfscValid = !form.bank_ifsc || /^[A-Z]{4}0[A-Z0-9]{6}$/.test(form.bank_ifsc.toUpperCase());

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">Bank & Payouts</h2>
        <p className="text-sm text-muted-foreground">Manage your bank account for receiving payments</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Landmark className="h-4 w-4 text-cyan-600" />
            Bank Account Details
          </CardTitle>
          <CardDescription>
            These details will be used for payouts and displayed on invoices
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Account Holder Name</Label>
              <Input
                value={form.bank_account_name}
                onChange={(e) => setForm(p => ({ ...p, bank_account_name: e.target.value }))}
                placeholder="Your Name or Business Name"
              />
            </div>
            <div className="space-y-2">
              <Label>Bank Name</Label>
              <Input
                value={form.bank_name}
                onChange={(e) => setForm(p => ({ ...p, bank_name: e.target.value }))}
                placeholder="HDFC Bank"
              />
            </div>
            <div className="space-y-2">
              <Label>Account Number</Label>
              <Input
                value={form.bank_account_number}
                onChange={(e) => setForm(p => ({ ...p, bank_account_number: e.target.value.replace(/\D/g, '') }))}
                placeholder="1234567890123"
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                IFSC Code
                {form.bank_ifsc && (
                  isIfscValid ? (
                    <CheckCircle className="h-3 w-3 text-green-600" />
                  ) : (
                    <AlertCircle className="h-3 w-3 text-destructive" />
                  )
                )}
              </Label>
              <Input
                value={form.bank_ifsc}
                onChange={(e) => setForm(p => ({ ...p, bank_ifsc: e.target.value.toUpperCase() }))}
                placeholder="HDFC0001234"
                className={form.bank_ifsc && !isIfscValid ? 'border-destructive' : ''}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Button onClick={handleSave} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700">
        {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
        Save Changes
      </Button>
    </div>
  );
}
